import { EnvService } from '../env.service';
import { MockDataFunc, MockResponseFunc } from '../../models';
import {
  createPatientInsurance,
  createPaymentMethod,
  createTestPatientNotificationMock,
  createToDo,
  editPaymentMethod,
  getAddresses,
  getAddressOrders,
  getCopay,
  getDatesOfService,
  getDaysOnHandRequired,
  getFrequenciesRequired,
  getHealthConditions,
  getInsuranceTypeReportGroups,
  getMedicalHistory,
  getNotifications,
  getOrderDetails,
  getPatient,
  getPatientBalance,
  getPatientContacts,
  getPatientInsurance,
  getPatientInsuranceCards,
  getPatients,
  getPaymentMethods,
  getProductGroupHcpcs,
  getProviders,
  getProviderVisits,
  getProviderVisitVerification,
  getRegistrationTestPatients,
  getRelationshipsToInsured,
  getReorderDetails,
  getTestPatients,
  getUsStates,
  loginPatient,
  makePayment,
  respondToNotification,
  revokePreviousTokens,
  getShipmentDetails,
  submitShipment,
  searchUsers,
  submitReorder,
  updateAddressOrders,
  updateMedicalHistory,
  updatePassword,
  updatePatient,
  updatePatientAddresses,
  updatePatientContactsAndConsent,
  updatePatientName,
  updateRegistrationTestPatientContacts,
  validateProviderVisit,
  getOrderStatusOrders,
  getPwpOrders,
  getShipmentCollectionStatus,
  setShipmentCollectionStatus,
  signInAgentWithOtp,
  setCgm90Enrollment,
  setTestPatientCopayExclusion,
  submitReorderError,
  createTestAgentOtp,
  setTestPatientDeductibleOverrides,
  getPharmOrders,
  submitPharmReorderError,
  submitPharmReorder,
  verifyActivationToken,
  resendActivationEmail,
  createUserFromEmail,
} from './http-mocks';
import { getPharmAddress } from './http-mocks/pharm-address.mock';
import { releaseShipment } from './http-mocks/release-shipment.mock';

export interface ApiUrlConfig {
  httpMethod: string;
  responseContentType?: string;
  endpoint: (envService: EnvService, urlParams?: any) => string;
  endpointRegex: (envService: EnvService) => string | RegExp;
  mockSuccessData: MockDataFunc;
  mockDelay?: number;
  mockResponse?: MockResponseFunc;
}

export const urls: { [key: string]: ApiUrlConfig } = {
  signInAgentWithOtp: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { otp: string }) =>
      `${envService.api.baseUrl}/public/agents/otp/${urlParams.otp}`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/public/agents/otp/(?<otp>.+)$`),
    mockSuccessData: signInAgentWithOtp,
  },
  createTestAgentOtp: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/testPatients/agents/otp`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/testPatients/agents/otp`,
    mockSuccessData: createTestAgentOtp,
  },
  getTestPatients: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/testPatients`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/testPatients`,
    mockSuccessData: getTestPatients,
  },
  resetTestPatientPaymentMethods: {
    httpMethod: 'DELETE',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/paymentMethods`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/paymentMethods$`),
    mockSuccessData: () => ({}),
  },
  expireTestPatientPaymentMethod: {
    httpMethod: 'PATCH',
    endpoint: (envService: EnvService, urlParams: { patientId: number; paymentMethodId: string }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/paymentMethods/${urlParams.paymentMethodId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/paymentMethods/(?<paymentMethodId>.+)$`),
    mockSuccessData: () => ({}),
  },
  getRegistrationTestPatients: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/testPatientsForRegistration`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/testPatientsForRegistration`,
    mockSuccessData: getRegistrationTestPatients,
  },
  updateRegistrationTestPatientContacts: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/updateTestPatientContacts`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/updateTestPatientContacts`,
    mockSuccessData: updateRegistrationTestPatientContacts,
  },
  clearTestPatientNotifications: {
    httpMethod: 'DELETE',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/notifications/clear`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/notifications/clear$`),
    mockSuccessData: () => ({}),
  },
  createTestPatientNotification: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/notifications`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/notifications$`),
    mockSuccessData: createTestPatientNotificationMock,
  },
  setTestPatientCopayExclusion: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/copayExclusion`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/copayExclusion$`),
    mockSuccessData: setTestPatientCopayExclusion,
  },
  setTestPatientDeductibleOverrides: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/testPatients/${urlParams.patientId}/deductibleOverrides`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/testPatients/(?<patientId>\\d+)/deductibleOverrides$`),
    mockSuccessData: setTestPatientDeductibleOverrides,
  },
  getPatients: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/user/getPatients`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/user/getPatients`,
    mockSuccessData: getPatients,
  },
  loginPatient: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/user/insertLoginHistory`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/user/insertLoginHistory`,
    mockSuccessData: loginPatient,
  },
  revokePreviousTokens: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/revokePreviousTokens`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/revokePreviousTokens`,
    mockSuccessData: revokePreviousTokens,
  },
  getPwpOrders: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/orders`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/orders$`),
    mockSuccessData: getPwpOrders,
  },
  getOrderStatusOrders: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/status/orders`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/status/orders$`),
    mockSuccessData: getOrderStatusOrders,
  },
  getOrderDetails: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number; orderId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/orders/${urlParams.orderId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/orders/(?<orderId>\\d+)$`),
    mockSuccessData: getOrderDetails,
  },
  getReorderDetails: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number; reorderId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/reorders/${urlParams.reorderId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/reorders/(?<reorderId>\\d+)$`),
    mockSuccessData: getReorderDetails,
  },
  getShipmentDetails: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number; shipmentId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/shipments/${urlParams.shipmentId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/shipments/(?<shipmentId>\\d+)$`),
    mockSuccessData: getShipmentDetails,
  },
  getShipmentCollectionStatus: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number; shipmentId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/shipments/${urlParams.shipmentId}/collectionStatus`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(
        `^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/shipments/(?<shipmentId>\\d+)/collectionStatus$`
      ),
    mockSuccessData: getShipmentCollectionStatus,
  },
  setShipmentCollectionStatus: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number; shipmentId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/shipments/${urlParams.shipmentId}/collectionStatus`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(
        `^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/shipments/(?<shipmentId>\\d+)/collectionStatus$`
      ),
    mockSuccessData: setShipmentCollectionStatus,
  },
  releaseShipment: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/shipments/releaseShipment`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/shipments/releaseShipment$`),
    mockSuccessData: releaseShipment,
  },
  getDaysOnHandRequired: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/daysOnHandRequired`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/daysOnHandRequired$`),
    mockSuccessData: getDaysOnHandRequired,
  },
  getFrequenciesRequired: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/frequenciesRequired`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/frequenciesRequired$`),
    mockSuccessData: getFrequenciesRequired,
  },
  getRelationshipsToInsured: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/lookup/a_patient_insurance/insured_relationship`,
    endpointRegex: (envService: EnvService) =>
      `${envService.api.baseUrl}/lookup/a_patient_insurance/insured_relationship`,
    mockSuccessData: getRelationshipsToInsured,
  },
  getPatientInsurance: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/insurances`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/insurances$`),
    mockSuccessData: getPatientInsurance,
  },
  getProviders: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number; shipmentTypeId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/physicians/shipmentTypes/${urlParams.shipmentTypeId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(
        `^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/physicians/shipmentTypes/(?<shipmentTypeId>\\d+)$`
      ),
    mockSuccessData: getProviders,
  },
  getProviderVisitVerification: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/physicianVisitsRequired`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/physicianVisitsRequired$`),
    mockSuccessData: getProviderVisitVerification,
  },
  getProviderVisits: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/physicianVisits`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/physicianVisits$`),
    mockSuccessData: getProviderVisits,
  },
  validateProviderVisit: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/physicianVisitValidation`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/physicianVisitValidation$`),
    mockSuccessData: validateProviderVisit,
  },
  getUsStates: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/usaStates`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/usaStates`,
    mockSuccessData: getUsStates,
  },
  getAddresses: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/addresses`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/addresses$`),
    mockSuccessData: getAddresses,
  },
  getCopay: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/copay`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/copay`,
    mockSuccessData: getCopay,
  },
  getDatesOfService: {
    httpMethod: 'PUT',
    // We are interested in *evaluate* dates of service directly
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/evaluateDatesOfService`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/evaluateDatesOfService`,
    mockSuccessData: getDatesOfService,
  },
  getPaymentMethods: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/paymentMethods`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/paymentMethods$`),
    mockSuccessData: getPaymentMethods,
  },
  editPaymentMethod: {
    httpMethod: 'PATCH',
    endpoint: (envService: EnvService, urlParams: { patientId: number; paymentMethodId: string }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/paymentMethods/${urlParams.paymentMethodId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/paymentMethods/(?<paymentMethodId>.+)$`),
    mockSuccessData: editPaymentMethod,
  },
  createPaymentMethod: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/paymentMethods`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/paymentMethods$`),
    mockSuccessData: createPaymentMethod,
  },
  getProductGroupHcpcs: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { hcpcGroupName: string }) =>
      `${envService.api.baseUrl}/lookup/productgrouphcpcs/${urlParams.hcpcGroupName}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/lookup/productgrouphcpcs/(?<hcpcGroupName>.+)$`),
    mockSuccessData: getProductGroupHcpcs,
  },
  getInsuranceTypeReportGroups: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { insuranceTypeName: string }) =>
      `${envService.api.baseUrl}/lookup/insurancetypereportgroups/${urlParams.insuranceTypeName}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/lookup/insurancetypereportgroups/(?<insuranceTypeName>.+)$`),
    mockSuccessData: getInsuranceTypeReportGroups,
  },
  submitReorder: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/submitPortalReorder`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/submitPortalReorder`,
    mockSuccessData: submitReorder,
    // mockResponse: submitReorderError, // Uncomment to test error case
  },
  submitPharmReorder: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/authorizePharmacyOrder`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/authorizePharmacyOrder`,
    mockSuccessData: submitPharmReorder,
  },

  submitReorderForAgent: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/submitPortalReorderForAgent`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/submitPortalReorderForAgent`,
    mockSuccessData: submitReorder,
  },
  submitShipment: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/submitExistingShipment`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/submitExistingShipment`,
    mockSuccessData: submitShipment,
  },
  makePayment: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/payments`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/payments$`),
    mockSuccessData: makePayment,
  },
  updatePatientAddresses: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/addresses`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/addresses$`),
    mockSuccessData: updatePatientAddresses,
  },
  createToDo: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/createToDo`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/createToDo`,
    mockSuccessData: createToDo,
  },
  createPatientInsurance: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/insurances`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/insurances$`),
    mockSuccessData: createPatientInsurance,
  },
  getPatientContacts: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/contacts`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/contacts$`),
    mockSuccessData: getPatientContacts,
  },
  updatePatientContactsAndConsent: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/contacts`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/contacts$`),
    mockSuccessData: updatePatientContactsAndConsent,
  },
  getPatientInsuranceCards: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/insuranceCards`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/insuranceCards$`),
    mockSuccessData: getPatientInsuranceCards,
  },
  getMedicalHistory: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/medicalHistory`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/medicalHistory$`),
    mockSuccessData: getMedicalHistory,
  },
  getHealthConditions: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/medicalAllergyConditions`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/medicalAllergyConditions$`),
    mockSuccessData: getHealthConditions,
  },
  updateMedicalHistory: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/medicalHistory`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/medicalHistory$`),
    mockSuccessData: updateMedicalHistory,
  },
  searchUsers: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/users`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/users`,
    mockSuccessData: searchUsers,
  },
  getAddressOrders: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/ordersForAddress`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/ordersForAddress$`),
    mockSuccessData: getAddressOrders,
  },
  updateAddressOrders: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/ordersForAddress`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/ordersForAddress$`),
    mockSuccessData: updateAddressOrders,
  },
  getPatientBalance: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/balance`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/balance$`),
    mockSuccessData: getPatientBalance,
  },
  getNotifications: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/notifications`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/notifications$`),
    mockSuccessData: getNotifications,
  },
  getPharmOrders: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/orders/${urlParams.patientId}`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/orders/(?<patientId>\\d+)$`),
    mockSuccessData: getPharmOrders,
  },
  getPharmAddress: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { pharmacy: string; patientId: number; customerAddressId: string }) =>
      `${envService.api.baseUrl}/customers/${urlParams.pharmacy}/${urlParams.patientId}/${urlParams.customerAddressId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(`^${envService.api.baseUrl}/customers/(?<pharmacy>.+)/(?<patientId>\\d+)/(?<customerAddressId>.+)$`),
    mockSuccessData: getPharmAddress,
  },
  getPatient: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)$`),
    mockSuccessData: getPatient,
  },
  updatePatientName: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)$`),
    mockSuccessData: updatePatientName,
  },
  updatePatient: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/patients/(?<patientId>\\d+)$`),
    mockSuccessData: updatePatient,
  },
  updatePassword: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/user/changePassword`,
    endpointRegex: (envService: EnvService) => new RegExp(`^${envService.api.baseUrl}/user/changePassword$`),
    mockSuccessData: updatePassword,
  },
  respondToNotification: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number; notificationId: number; responseId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/notifications/${urlParams.notificationId}/respond/${urlParams.responseId}`,
    endpointRegex: (envService: EnvService) =>
      new RegExp(
        `^${envService.api.baseUrl}/patients/(?<patientId>\\d+)/notifications/(?<notificationId>\\d+)/respond/(?<responseId>\\d+)$`
      ),
    mockSuccessData: respondToNotification,
  },
  setCgm90Enrollment: {
    httpMethod: 'PUT',
    endpoint: (envService: EnvService, urlParams: { patientId: number }) =>
      `${envService.api.baseUrl}/patients/${urlParams.patientId}/cgmEnrollment`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/patients/(?<patientId>\\d+)/cgmEnrollment`,
    mockSuccessData: setCgm90Enrollment,
  },
  activateUser: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/user/activate`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/user/activate`,
    mockSuccessData: () => ({}),
  },
  verifyActivationToken: {
    httpMethod: 'GET',
    endpoint: (envService: EnvService, urlParams: { token: string }) =>
      `${envService.api.baseUrl}/public/user/verifyActivationToken/${urlParams.token}`,
    endpointRegex: (envService: EnvService) =>
      `${envService.api.baseUrl}/public/user/verifyActivationToken/(?<token>.+)`,
    mockSuccessData: verifyActivationToken,
  },
  resendActivationEmail: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/public/user/resendActivationEmail`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/public/user/resendActivationEmail`,
    mockSuccessData: resendActivationEmail,
  },
  createUserFromEmail: {
    httpMethod: 'POST',
    endpoint: (envService: EnvService) => `${envService.api.baseUrl}/public/user/createUserFromEmail`,
    endpointRegex: (envService: EnvService) => `${envService.api.baseUrl}/public/user/createUserFromEmail`,
    mockSuccessData: createUserFromEmail,
  },
};
